// The dossier's team and the RACI matrix by chapter. Escalations of missing
// information go to the Responsible (R) of the section's chapter.
import { useState } from "react";
import { UserPlus, X } from "lucide-react";
import { api } from "../api/client";
import { invalidate, useMutation, useResource } from "../api/resource";
import type { Contributor, RaciRole } from "../api/types";
import { EmptyState, ErrorState, Notice, PageHeader, SkeletonBlock, StatusBadge } from "../components/ui";
import { useCurrentUser } from "../auth";
import { keys, useDossier } from "../dossier";
import { w } from "../i18n";

const ROLES: RaciRole[] = ["R", "A", "C", "I"];

function AddContributor({ onDone }: { onDone: () => void }) {
  const { dossier } = useDossier();
  const [form, setForm] = useState({ name: "", email: "", role: "", department: "" });
  const add = useMutation(api.addContributor);
  const submit = async () => {
    if (await add.run(dossier.id, form)) { setForm({ name: "", email: "", role: "", department: "" }); onDone(); }
  };
  const field = (key: keyof typeof form, type = "text") => <div className="ws-field" style={{ margin: 0 }}>
    <label htmlFor={`ctb-${key}`}>{w(`team.field.${key}`)}</label>
    <input id={`ctb-${key}`} className="ws-input" type={type} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} />
  </div>;
  return <div className="ws-card-pad">
    <div className="ws-team-form">{field("name")}{field("email", "email")}{field("role")}{field("department")}
      <button className="ws-btn primary" disabled={add.pending || !form.name.trim() || !form.email.trim()} onClick={submit}>
        <UserPlus size={16} />{w("team.add")}</button></div>
    {add.error && <ErrorState title={w("team.addError")} error={add.error} />}
  </div>;
}

function RoleCell({ scope, role, assigned, contributors, onChange, busy }: {
  scope: string; role: RaciRole; assigned: string[]; contributors: Contributor[];
  onChange: (scope: string, role: RaciRole, ids: string[]) => void; busy: boolean;
}) {
  const name = (id: string) => contributors.find(c => c.id === id)?.name || id;
  const active = contributors.filter(c => c.active);
  if (role === "R" || role === "A") {
    return <select className="ws-select" aria-label={`${scope} ${role}`} value={assigned[0] || ""} disabled={busy}
      onChange={e => onChange(scope, role, e.target.value ? [e.target.value] : [])}>
      <option value="">{w("team.nobody")}</option>
      {active.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>;
  }
  const available = active.filter(c => !assigned.includes(c.id));
  return <div className="ws-chips">
    {assigned.map(id => <span key={id} className="ws-chip">{name(id)}
      <button aria-label={w("team.remove", { name: name(id) })} disabled={busy} onClick={() => onChange(scope, role, assigned.filter(x => x !== id))}><X size={12} /></button></span>)}
    {available.length > 0 && <select className="ws-select sm" aria-label={`${scope} ${role}`} value="" disabled={busy}
      onChange={e => e.target.value && onChange(scope, role, [...assigned, e.target.value])}>
      <option value="">{w("team.addPerson")}</option>{available.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
  </div>;
}

export function TeamPage() {
  const { dossier } = useDossier();
  const team = useResource(keys.team(dossier.id), () => api.team(dossier.id));
  const raci = useMutation(api.setRaci);
  const toggle = useMutation(api.setContributorActive);
  const refresh = () => invalidate(keys.team(dossier.id), keys.audit(dossier.id), "requests:");
  const onChange = async (scope: string, role: RaciRole, ids: string[]) => { if (await raci.run(dossier.id, scope, role, ids)) refresh(); };
  const data = team.data;
  const { can } = useCurrentUser();
  const manage = can("approver");

  return <>
    <PageHeader title={w("team.title")} lead={w("team.lead")} />
    {team.error && <ErrorState title={w("common.genericError")} error={team.error} onRetry={team.reload} />}
    {(raci.error || toggle.error) && <ErrorState title={w("team.saveError")} error={raci.error || toggle.error} />}
    {team.loading && <SkeletonBlock lines={8} />}
    {data && !manage && <div style={{ marginBottom: 12 }}><Notice tone="neutral">{w("auth.needs", { role: w("role.user.approver") })}</Notice></div>}
    {data && <div className="ws-stack">
      <section className="ws-card">
        <div className="ws-card-head"><h2>{w("team.contributors")}</h2><span className="ws-small ws-muted">{w("team.count", { count: data.contributors.length })}</span></div>
        {!data.contributors.length && <EmptyState title={w("team.empty")} body={w("team.emptyBody")} />}
        {data.contributors.length > 0 && <div className="ws-table-wrap"><table className="ws-table">
          <thead><tr><th>{w("team.field.name")}</th><th>{w("team.field.email")}</th><th>{w("team.field.role")}</th>
            <th>{w("team.field.department")}</th><th>{w("team.status")}</th><th><span className="ws-sr-only">{w("issues.col.action")}</span></th></tr></thead>
          <tbody>{data.contributors.map(c => <tr key={c.id}>
            <td><strong>{c.name}</strong></td><td className="ws-small">{c.email}</td><td className="ws-small">{c.role || ""}</td>
            <td className="ws-small">{c.department || ""}</td>
            <td><StatusBadge status={c.active ? "ready" : "not_started"} label={w(c.active ? "team.active" : "team.inactive")} /></td>
            <td style={{ textAlign: "right" }}><button className="ws-btn sm" disabled={toggle.pending || !manage}
              onClick={async () => { if (await toggle.run(dossier.id, c.id, !c.active)) refresh(); }}>
              {w(c.active ? "team.deactivate" : "team.activate")}</button></td>
          </tr>)}</tbody></table></div>}
        {manage && <AddContributor onDone={refresh} />}
      </section>

      <section className="ws-card">
        <div className="ws-card-head"><h2>{w("team.raci")}</h2><span className="ws-small ws-muted">{w("team.raciHint")}</span></div>
        <div className="ws-table-wrap"><table className="ws-table ws-raci">
          <thead><tr><th>{w("team.scope")}</th>{ROLES.map(role => <th key={role} title={w(`team.role.${role}`)}>{role} · {w(`team.role.${role}`)}</th>)}</tr></thead>
          <tbody>{data.scopes.map(scope => <tr key={scope.code}>
            <td><span className="ws-mono">{scope.code === "SYN" ? "" : scope.code}</span> {scope.title}</td>
            {ROLES.map(role => <td key={role}><RoleCell scope={scope.code} role={role} assigned={data.raci[scope.code]?.[role] || []}
              contributors={data.contributors} onChange={onChange} busy={raci.pending || !manage} /></td>)}
          </tr>)}</tbody></table></div>
      </section>
      <Notice tone="neutral">{w("team.note")}</Notice>
    </div>}
  </>;
}
